import { FormField, Input, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  getLicenseDetailQueryKey,
  getLicenseStatusQueryKey,
  type LicenseResponse,
  updateLicenseMutation,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { type LicenseFormValues, licenseFormSchema, toFormValues, toUpdateBody } from "./licenseValues";

function Hint({ children }: Readonly<{ children: string }>) {
  return (
    <Text variant="caption" tone="tertiary">
      {children}
    </Text>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (value: string) => void }>) {
  const id = useId();
  return (
    <FormField label={label} htmlFor={id}>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}

/** Inline edit form for the license configuration (install admins). */
export function LicenseForm({ license, onDone }: Readonly<{ license: LicenseResponse; onDone: () => void }>) {
  const { t } = useTranslation("adminLicense");
  const toast = useToast();
  const queryClient = useQueryClient();
  const update = useMutation(updateLicenseMutation());

  const form = useAppForm({
    defaultValues: toFormValues(license) as LicenseFormValues,
    validators: { onChange: licenseFormSchema(t("form.seatLimitInvalid")) },
    onSubmit: async ({ value }) => {
      try {
        const res = await update.mutateAsync({ body: toUpdateBody(value), throwOnError: true });
        queryClient.setQueryData(getLicenseDetailQueryKey(), res);
        await queryClient.invalidateQueries({ queryKey: getLicenseStatusQueryKey() });
        toast.success(t("form.saved"));
        onDone();
      } catch (error) {
        toast.error(errorMessage(error, t("form.saveFailed")));
      }
    },
  });

  return (
    <form
      aria-label={t("form.label")}
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppField name="licenseName">{(field) => <field.TextField label={t("config.name")} />}</form.AppField>
      <div className="flex flex-col gap-1">
        <form.AppField name="seatLimit">
          {(field) => <field.TextField label={t("config.seatLimit")} placeholder={t("config.unlimited")} />}
        </form.AppField>
        <Hint>{t("form.seatLimitHint")}</Hint>
      </div>
      <div className="flex flex-col gap-1">
        <form.Field name="expiresAt">
          {(field) => (
            <DateInput label={t("config.expiresAt")} value={field.state.value} onChange={field.handleChange} />
          )}
        </form.Field>
        <Hint>{t("form.expiresAtHint")}</Hint>
      </div>
      <div className="flex flex-col gap-1">
        <form.AppField name="exemptDomains">
          {(field) => (
            <field.TextField label={t("config.exemptDomains")} placeholder={t("form.exemptDomainsPlaceholder")} />
          )}
        </form.AppField>
        <Hint>{t("form.exemptDomainsHint")}</Hint>
      </div>
      <form.AppField name="notes">{(field) => <field.TextareaField label={t("config.notes")} />}</form.AppField>
      <form.AppForm>
        <form.SubmitActions onCancel={onDone} />
      </form.AppForm>
    </form>
  );
}
